import { createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { constants } from 'config/constants';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { selectDevices, selectDevicesBucketState } from 'reduxStore/devices/selectors';
import { selectFloors } from 'reduxStore/floors/selectors';
import { selectSites } from 'reduxStore/sites/selectors';
import { selectCurrentProfileOrgSettings } from 'reduxStore/settings/selectors';
import { planState } from './planState';
import { BUCKET_MINUTES, CLIP_MAX_MINUTES, MIN_LOOKBACK_DAYS, eventsKey } from './slice';
import { LIVE_CODEC as CODECS, buildVerkadaStreamUrl } from './projection';

// ── The live stream's shape, and the two knobs that set it ──────────────────────
//
// Both are Verkada's own defaults now. They were `h264` + `high_res` inherited from the
// server's builder, which cost a transcode per segment and a bigger segment to ship —
// together most of the ~10 s an operator saw behind the live edge.
//
// ⚠️ `hevc` plays only where the browser decodes HEVC in MSE: Safari, and Chrome/Edge on
// hardware that can. Where it cannot, the playlist loads and no picture appears. If that
// is your fleet, `CODECS.compatible` is the one word to change.
const LIVE_CODEC = CODECS.native;
const LIVE_RESOLUTION = 'low_res';

// Camera selectors — EVERY derivation the Cameras page performs.
//
// Components render what these return. Bucket maths, grouping, the in/out-of-window
// colouring, durations, the "plays in parts" flag and the empty-state reasons all
// live here, so a card and the list underneath it cannot disagree: they read the
// same function.

/**
 * The longest window one history request may ask for.
 *
 * 🛑 NOTHING ENFORCES THIS BUT US. It was a server guard until 2026-09-07; now the
 * browser builds the url, so exceeding Verkada's 3600 s reaches them directly and they
 * answer 500 "Error Fetching Historical Video m3u8" — which reads as a broken camera.
 * The real number is the provider's (`maxHistoryWindowSeconds`); this is the split size.
 */
export const HISTORY_CHUNK_MINUTES = 60;

const selectCamerasState = (state) => state.Cameras;
const selectUi = (state) => state.Cameras.ui;

/**
 * `Auth.selectedProfile.siteID`, NOT `App.selectedSiteID`. The two stay in step, but the
 * profile one is what Devices and People key on; mixing them is a permanent bucket miss.
 */
const selectScopeKey = (state) => state.Auth?.selectedProfile?.siteID ?? 'all';

// ── Cameras ─────────────────────────────────────────────────────────────────────
//
// 🛑 THERE IS NO CAMERA REQUEST AND NO CAMERA BUCKET. A camera is a device row like any
// other — it comes back on the same `GET /devices` the shell already fires for every
// scope, and `getAllDevicesAllLocations` places it — so this page derives its list from
// `state.Devices` rather than asking again.
//
// What it used to do was fetch three times over: `/devices?defaultCategoryID=15`,
// `/devices?defaultCategoryID=23` and `getAllDevicesAllLocations` — the first two strict
// subsets of the devices read, the third byte-for-byte the same request. Both were
// dispatched from the SAME `onScopeChange`, in parallel, on every scope change.
//
// ⚠️ Deleting the request also deleted a boot race that the duplication was hiding.
// `onScopeChange` reads the camera module flag off org settings that may not have landed
// (`useScopeUrlSync` fires `setSelectedSite` as soon as SITES arrive), so it read `false`,
// never dispatched the camera read, and — having already written `hydratedScopeKey` —
// could never run again for that scope. The page then fetched for itself on mount, which
// is the loading screen operators saw on a dashboard that had finished loading. Devices
// are a hard requirement of `selectAccessDashboardReady`, so derived cameras cannot be
// late: the route does not mount until they are in the store.
const CAMERA_CATEGORY_IDS = [constants.VERKADA_CAMERAS, constants.VORTEX_CAMERAS];

/**
 * `plan` needs a map AND both axes: a mapID with null coordinates is a plan mid-setup,
 * and counting it as placed reports a pin the floor plan cannot draw.
 */
const placementOfRow = (row) => {
	if (row.mapID != null && row.xAxis != null && row.yAxis != null) return 'plan';
	return 'space';
};

/**
 * Most-placed row first, ties broken by locationID. Device.Location is keyed by
 * (deviceID, siteID), so a camera at two sites has two rows and would place twice.
 *
 * ⚠️ Only FLOOR rows reach here. The Devices slice keeps `location` for rows with a
 * floorID and reduces the rest to `sites`, so 'site' and 'unallocated' are decided by the
 * caller below rather than by ranking.
 */
const bestLocation = (rows) => {
	if (!rows?.length) return null;
	const rank = (row) => (placementOfRow(row) === 'plan' ? 0 : 1);
	return [...rows].sort((a, b) => rank(a) - rank(b) || (a.locationID ?? 0) - (b.locationID ?? 0))[0];
};

/**
 * One device row, read as a camera.
 *
 * The two things a camera says that a device row does not: `online`, because a device row
 * calls it `status` and without the bridge the field is undefined — which IS the third
 * state, so every dot silently greys — and `placement`, which is the four-level answer the
 * rail groups by and the stat row counts.
 */
const toCamera = (device) => {
	const location = bestLocation(device.location);
	const siteID = location?.siteID ?? device.sites?.[0]?.siteID ?? null;
	return {
		...device,
		online: device.status ?? null,
		siteID,
		floorID: location?.floorID ?? null,
		mapID: location?.mapID ?? null,
		xAxis: location?.xAxis ?? null,
		yAxis: location?.yAxis ?? null,
		// A device with no floor row and no site is not placed anywhere; with a site and no
		// floor it is placed AT the site, which is a real answer and not an absence.
		placement: location ? placementOfRow(location) : siteID != null ? 'site' : 'unallocated'
	};
};

/**
 * The cameras for the scope on screen.
 *
 * 🛑 Already narrowed — /devices filtered by site in SQL. Nothing downstream re-filters,
 * and that is the point of the bucket: when the tree narrowed one way and the cards
 * another, the numbers above the list stopped describing the list.
 *
 * `selectDevices` sorts A-Z by name, which is the order this page has always listed in.
 */
export const selectCameras = createSelector([selectDevices], (devices) =>
	devices.filter((device) => CAMERA_CATEGORY_IDS.includes(device.defaultCategoryID)).map(toCamera)
);

// Plain lookups, not createSelector: a parameterised createSelector has a cache size
// of 1, so N rows calling it with different deviceIDs in one render pass defeat the
// memo entirely.
export const selectCameraByDeviceID = (state, deviceID) =>
	selectCameras(state).find((camera) => camera.deviceID === deviceID) || null;

export const selectSelectedCamera = (state) =>
	state.Cameras.ui.selectedDeviceID == null ? null : selectCameraByDeviceID(state, state.Cameras.ui.selectedDeviceID);

/**
 * Still waiting on the devices read, OR on the locations that place its rows.
 *
 * 🛑 BOTH halves, and the second is the one that is easy to miss. `/devices` answers
 * first and clears `loading`; `getAllDevicesAllLocations` patches `location` and `sites`
 * onto the rows afterwards. In between, every camera is real and every camera is
 * `unallocated` — so a page that stopped waiting at `loading: false` would draw a full
 * wall under a rail claiming nothing is placed, then rearrange it.
 */
export const selectCamerasLoading = createSelector(
	[selectDevicesBucketState],
	(bucket) => bucket.loading || !bucket.locationsLoaded
);

// The cards and the list load together — both are the one /devices read.
export const selectMetricsLoading = selectCamerasLoading;
export const selectCamerasUi = selectUi;

/** The moment picked on the chart, as epoch seconds — the vertical bar. See `ui.seekTo`. */
export const selectPickedMoment = (state) => state.Cameras.ui.seekTo;

/** Is the chart bracketing a clip right now — see `ui.clipMode`. */
export const selectIsClipMode = (state) => state.Cameras.ui.clipMode === true;

/*
 * 🛑 `selectActivityTab` STOOD HERE AND IS GONE WITH THE SIDE IT CHOSE BETWEEN. The activity
 * panel was Events | Clips; the events list has been withdrawn from every camera (see
 * ShowActivityCameraPanel), so the panel has one pane and nothing left to select. `ui.activityTab`
 * survives in the slice — `requestClip` still writes 'clips' to it — because the day a second
 * pane returns, the press that creates a row must still be able to open the list it lands in.
 */

/**
 * Has this ORG any cameras at all — the question the empty state asks, not the scope's.
 *
 * 🛑 TWO WAYS TO ANSWER YES, and the first one is the one that must never be dropped: if
 * the scope on screen is holding cameras, the org has cameras. That is not an inference,
 * it is the cameras themselves. The stored flag is only needed for the case the scope
 * genuinely cannot answer — a SITE that holds none while the org holds two hundred — which
 * is why the probe that sets it runs in a site view and nowhere else.
 *
 * ⚠️ Reading the flag ALONE is what put "No cameras found — connect a camera integration"
 * over a full wall on every reload of the portfolio view. Nothing fills the flag there:
 * the probe is site-scoped, and the scoped camera read that used to set it in passing does
 * not exist any more — the list is derived from devices now. A page that can see forty
 * cameras must never be able to say the org has none.
 */
export const selectOrgHasCameras = createSelector(
	[(state) => state.Cameras.orgHasCameras === true, selectCameras],
	(probed, cameras) => probed || cameras.length > 0
);

/**
 * The last failure, whatever asked for it.
 *
 * 🛑 Read alongside a loading flag, never instead of one. A flag says "something is on its
 * way"; this says "the answer already came back and it was a refusal". A surface that
 * watches only the flag can sit on a spinner over an answer it already has.
 */
export const selectCamerasError = (state) => state.Cameras.error ?? null;

/**
 * The ONE loading question this page asks: are we still waiting for the first cameras?
 *
 * 🛑 Every surface reads this rather than the raw flag, so "no permanent loading state" is
 * one rule in one place instead of four that can drift. Three conditions, and each removes
 * a way a spinner could outlive its request:
 *
 * - `loading` — something is in flight at all.
 * - `length === 0` — we hold nothing to draw. With cameras in hand a refresh is invisible;
 *   swapping a populated wall back to a spinner every poll is worse than a stale tile.
 * - `!error` — the flag is a promise that an answer is ON ITS WAY, and a request that has
 *   already come back a failure is not. Without this the page could sit spinning for the
 *   life of the tab over an answer it already had, which is the one thing a loading state
 *   must never do. What it falls through to is the empty state, which is an answer.
 */
export const selectIsAwaitingFirstCameras = createSelector(
	[selectCameras, selectCamerasLoading, selectCamerasError],
	(cameras, loading, error) => loading && cameras.length === 0 && !error
);

/**
 * Counted from the SAME array the page renders — a second count over a second query is
 * how "8 cameras" ends up above a list of seven. Counters are seeded from the
 * vocabularies, so a new placement cannot silently increment `undefined`.
 *
 * @param {Array} cameras
 * @returns {Object} total, liveness split, placement split, capability counts, scope sizes
 */
const computeMetrics = (cameras) => {
	const count = (predicate) => cameras.filter(predicate).length;
	const byPlacement = {
		unallocated: count((c) => c.placement === 'unallocated'),
		site: count((c) => c.placement === 'site'),
		space: count((c) => c.placement === 'space'),
		plan: count((c) => c.placement === 'plan')
	};

	return {
		total: cameras.length,
		online: count((c) => c.online === true),
		// 🛑 TWO states, and `null` counts as OFFLINE. The server answers
		// `true | false | null`, but a camera nothing has ever reported on is not
		// watching anything, and this product says so in one word rather than offering
		// an "Unknown" nobody can act on. Written `!== true` and not `=== false` so the
		// two counts always sum to `total` — a third silent bucket is exactly how a
		// donut ends up not filling its ring.
		offline: count((c) => c.online !== true),
		siteCount: new Set(cameras.map((c) => c.siteID).filter((id) => id != null)).size,
		spaceCount: new Set(cameras.map((c) => c.floorID).filter((id) => id != null)).size,
		// "On the floor plan" means PINNED. A camera with a space but no coordinate is
		// waiting to be placed, not on the plan.
		onPlan: byPlacement.plan,
		offPlan: cameras.length - byPlacement.plan,
		byPlacement,
		capabilities: {
			live: count((c) => c.capabilities?.live),
			history: count((c) => c.capabilities?.history),
			events: count((c) => c.capabilities?.events),
			share: count((c) => c.capabilities?.share),
			export: count((c) => c.capabilities?.export)
		}
	};
};

/**
 * Org-wide counts. Null while the first read is in flight: a page of honest zeroes reads
 * as "your cameras are gone", so the cards stay on their skeletons instead.
 */
export const selectMetrics = createSelector([selectCameras, selectCamerasLoading], (cameras, loading) =>
	cameras.length === 0 && loading ? null : computeMetrics(cameras)
);

// ── Capabilities ────────────────────────────────────────────────────────────────
//
// 🛑 The one rule of this surface: never branch on the vendor. The day Vivotek
// exposes playback the server flips `history` to true and the button appears with no
// change here. A `provider === 'verkada'` test anywhere turns that day into a ticket.

/**
 * What a camera can do, AND whether the integration behind it is still switched on.
 *
 * 🛑 TWO GATES, because the server's answer to the first one outlives the second. The
 * per-camera `capabilities` come from the provider contract and a linked `externalID` —
 * they say what the hardware and the vendor's API support. They do NOT ask whether this
 * org still has that integration enabled, and those come apart in a real state: an org
 * that ran Verkada, synced two hundred cameras, then switched the module off keeps every
 * one of those device rows, each still carrying `history: true`. With VORTEX on, the page
 * is `ready` and would draw a full timeline over cameras whose endpoints the org has
 * turned off — failing as an empty chart and a stream that never mints, rather than as a
 * feature that is not connected.
 *
 * ⚠️ This is NOT the vendor test the rule above forbids. Nothing here names Verkada: it
 * asks whether the module powering THIS camera's own provider is on, so the day Vivotek
 * arrives it needs a settings id and nothing else. The forbidden version is the one that
 * hardcodes which vendor a feature belongs to.
 */
const capabilityOf = (state, deviceID, key) => {
	const camera = selectCameraByDeviceID(state, deviceID);
	if (!camera || camera.capabilities?.[key] !== true) return false;
	return selectConfiguredProviders(state)[camera.provider] === true;
};

export const selectCanLookBack = (state, deviceID) => capabilityOf(state, deviceID, 'history');

/**
 * The longest clip this camera can produce, in seconds — the vendor's own limit.
 *
 * 🛑 Verkada allows 1200 and VORTEX exactly 30, so a single constant would either forbid a
 * legitimate Verkada clip or let an operator draw a VORTEX one the server refuses.
 */
export const selectMaxClipSeconds = (state, deviceID) =>
	selectCameraByDeviceID(state, deviceID)?.maxClipSeconds ?? CLIP_MAX_MINUTES * 60;

/**
 * How far back the calendar may reach on the camera that is open, in days.
 *
 * 🛑 THIS IS NOT `MAX_RANGE_DAYS` AND IT IS THE FIX FOR THE BUG THAT MOTIVATED IT. The
 * picker used one number for both "how wide may one read be" and "how old may a date be",
 * so a one-day read cap silently became a one-day archive: every date before yesterday was
 * greyed out on cameras holding a month of footage.
 *
 * It is the camera's OWN retention because retention is a per-camera promise — 30 days on
 * one plan, a year on another — and a fixed number would be wrong in both directions at
 * once. `MIN_LOOKBACK_DAYS` is the floor under it, for the cameras that report no retention
 * at all: a null there means no sync has written the field, which is a fact about us, and
 * refusing to look back on it would answer a question about their footage with it.
 *
 * ⚠️ Reaching this far back is not a promise that footage EXISTS there — retention is what
 * is meant to be kept, not a record of what was. The vendor answering "nothing here" is a
 * true answer an operator can act on; a greyed-out calendar over real footage is not.
 */
export const selectLookbackDays = createSelector([selectSelectedCamera], (camera) =>
	Math.max(camera?.retentionDays ?? 0, MIN_LOOKBACK_DAYS)
);

export const selectCanShare = (state, deviceID) => capabilityOf(state, deviceID, 'share');

/**
 * Can this camera's past be cut into a file? True on both vendors, by different mechanisms.
 * Tested as a CAPABILITY, never a vendor name — the server owns which provider offers what.
 */
export const selectCanExport = (state, deviceID) => capabilityOf(state, deviceID, 'export');

export const selectIsOnFloorPlan = (state, deviceID) => selectCameraByDeviceID(state, deviceID)?.placement === 'plan';

export const selectNeedsPlacing = (state, deviceID) => {
	const camera = selectCameraByDeviceID(state, deviceID);
	return camera ? camera.placement !== 'plan' : false;
};

/** floorID → name. The one lookup `planState` needs and cannot do for itself. */
export const selectSpaceNames = createSelector(
	[selectFloors],
	(floors) => new Map(floors.map((floor) => [floor.floorID, floor.name]))
);

/**
 * Where a camera stands with a floor plan, and every word said about it.
 *
 * 🛑 Read this rather than testing `placement` at a call site. `selectIsOnFloorPlan` and
 * `selectNeedsPlacing` above answer one bit each and are kept for the two callers that
 * only need a bit; anything that PRINTS something — a button label, a tooltip, a banner,
 * the mode a modal opens in — must come through here, or the modal opens in View mode
 * under a banner asking for a drag.
 */
export const selectPlanStateByDeviceID = (state, deviceID) => {
	const camera = selectCameraByDeviceID(state, deviceID);
	return planState(camera, camera?.floorID != null ? (selectSpaceNames(state).get(camera.floorID) ?? null) : null);
};

export const selectSelectedPlanState = createSelector([selectSelectedCamera, selectSpaceNames], (camera, names) =>
	planState(camera, camera?.floorID != null ? (names.get(camera.floorID) ?? null) : null)
);

/**
 * Whether a feature exists for the ORG at all; the per-camera flags decide each tile.
 * `events: 0` means an empty chart would claim "nothing happened", which is false.
 */
export const selectFleetCapabilities = createSelector(
	[selectMetrics],
	(metrics) => metrics?.capabilities || { live: 0, history: 0, events: 0, share: 0, export: 0 }
);

// ── Connection state ────────────────────────────────────────────────────────────

/**
 * Read from the org SETTINGS, not the sync-status routes: those are adminOnly, so a
 * site-scoped user 403s and would be shown a setup card for a working integration.
 */
export const selectConfiguredProviders = createSelector([selectCurrentProfileOrgSettings], (orgSettings) => {
	const isOn = (defaultSettingID) =>
		orgSettings.some((s) => s.defaultSettingID === defaultSettingID && Number(s.value) === 1);
	return {
		verkada: isOn(CONSTANTS_SETTINGS.VERKADA_MODULE),
		vortex: isOn(CONSTANTS_SETTINGS.VORTEX_MODULE)
	};
});

/**
 * `none` | `synced-never` | `ready`. Nothing renders above `ready` but the setup card.
 *
 * ⚠️ Derived from the MODULE FLAGS and metrics, deliberately not from the provider
 * sync status: /verkada and /vortex are adminOnly on the server, so a site-scoped
 * user 403s on them. Deriving connection state from a call half the users cannot
 * make would show them a setup card for an integration that is already connected.
 */
export const selectConnectionState = createSelector(
	[selectConfiguredProviders, selectOrgHasCameras],
	(configured, orgHasCameras) => {
		if (!configured.verkada && !configured.vortex) return 'none';
		// 🛑 The ORG flag, never the bucket. A site an operator is standing in may hold no
		// cameras while the org holds two hundred; reading the bucket here answered that
		// with "connect an integration", which is a false statement about their account.
		if (!orgHasCameras) return 'synced-never';
		return 'ready';
	}
);

export const selectSources = (state) => state.Cameras.sources;

// ── The camera browser tree ─────────────────────────────────────────────────────

const matchesFilter = (camera, filter) => {
	if (filter === 'online') return camera.online === true;
	if (filter === 'offline') return camera.online === false;
	if (filter === 'unplaced') return camera.placement !== 'plan';
	return true;
};

/**
 * Site › Space › Camera. Names are joined from the sites and floors already in the store.
 * Grouping FROM the cameras is what makes "no empty headings" impossible to get wrong;
 * cameras with no site fall into an orphan bucket, the one group needing action.
 */
const buildTree = (visible, sites, floors, { withEmptySpaces }) => {
	const siteName = new Map(sites.map((s) => [s.siteID, s.name]));
	const floorName = new Map(floors.map((f) => [f.floorID, f.name]));

	const bySite = new Map();
	for (const camera of visible) {
		const siteKey = camera.siteID ?? 'orphan';
		if (!bySite.has(siteKey)) {
			bySite.set(siteKey, {
				key: siteKey,
				siteID: camera.siteID ?? null,
				name: camera.siteID == null ? 'Not on a floor plan' : siteName.get(camera.siteID) || 'Unknown site',
				isOrphanBucket: camera.siteID == null,
				spaces: new Map(),
				count: 0
			});
		}
		const site = bySite.get(siteKey);
		site.count += 1;

		const spaceKey = camera.floorID ?? 'no-space';
		if (!site.spaces.has(spaceKey)) {
			site.spaces.set(spaceKey, {
				key: `${siteKey}:${spaceKey}`,
				floorID: camera.floorID ?? null,
				// A group with no space has NO NAME, and the panel draws no heading for it.
				// Naming it ("No space yet", "No Space") states an absence the row below
				// already states as something the operator can act on — its "Add to floor
				// plan" chip, which is also the fix. Position carries the grouping instead —
				// see bySpaceOrder.
				name: camera.floorID != null ? floorName.get(camera.floorID) || 'Unknown space' : null,
				cameras: []
			});
		}
		site.spaces.get(spaceKey).cameras.push(camera);
	}

	/*
	 * 🛑 A SITE'S OTHER SPACES COME TOO, cameras or not. Building the groups purely from
	 * the cameras meant a floor with nothing on it did not exist on this page — and "this
	 * building has a Level 1 and nobody has put a camera on it" is the single most useful
	 * thing the wall can tell an installer. The empty ones are drawn as an empty space,
	 * not as an absence to be inferred from a gap in the list.
	 *
	 * ⚠️ The CALLER decides. On the wall's tree they are dropped under a search or a
	 * status filter — an empty space matches nothing by definition, so keeping it would
	 * answer "show me the offline ones" with a floor that has no cameras at all. The
	 * rail's tree always keeps them: it is a map of the scope, not of the results.
	 */
	if (withEmptySpaces) {
		for (const site of bySite.values()) {
			if (site.siteID == null) continue;
			for (const floor of floors) {
				if (floor.siteID !== site.siteID || site.spaces.has(floor.floorID)) continue;
				site.spaces.set(floor.floorID, {
					key: `${site.key}:${floor.floorID}`,
					floorID: floor.floorID,
					name: floor.name || 'Unknown space',
					cameras: []
				});
			}
		}
	}

	const byName = (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

	// Alphabetical, but the space-less group ALWAYS last — by name it would land
	// wherever the alphabet put it, differently on each site.
	const bySpaceOrder = (a, b) => {
		if ((a.floorID == null) !== (b.floorID == null)) return a.floorID == null ? 1 : -1;
		return byName(a, b);
	};

	return (
		[...bySite.values()]
			.map((site) => ({
				...site,
				spaces: [...site.spaces.values()]
					.map((space) => ({ ...space, cameras: [...space.cameras].sort(byName) }))
					.sort(bySpaceOrder)
			}))
			// The orphan bucket sits last: it is a to-do list, not a place.
			.sort((a, b) => (a.isOrphanBucket === b.isOrphanBucket ? byName(a, b) : a.isOrphanBucket ? 1 : -1))
	);
};

/**
 * The tree the WALL draws: narrowed by the search box and the status filter.
 */
export const selectCameraTree = createSelector(
	[selectCameras, selectSites, selectFloors, selectUi],
	(cameras, sites, floors, ui) => {
		const search = ui.search.trim().toLowerCase();

		// No site filter here any more — `cameras` IS the scope's bucket, filtered by the
		// server. Re-narrowing it was the old shape's tax, and the bug it carried.
		const visible = cameras.filter((camera) => {
			if (!matchesFilter(camera, ui.filter)) return false;
			if (!search) return true;
			// 🛑 The camera's name only. Site/space matching needed a siteID and floorID that
			// resolve, and where they did not the wall just stopped answering.
			// ⚠️ Paired with SearchCameraInput's placeholder — widen both or neither.
			return String(camera.name ?? '')
				.toLowerCase()
				.includes(search);
		});

		return buildTree(visible, sites, floors, { withEmptySpaces: !search && ui.filter === 'all' });
	}
);

/**
 * The same tree the RAIL draws — and it ignores the search box and the status filter.
 *
 * 🛑 The rail is a map of the SCOPE, not of the results. Reading the wall's tree, a search
 * for something that matched nothing emptied it to a blank white card, and one that matched
 * a single camera collapsed the whole left-hand column to one row: the navigation danced
 * under the operator's hands as they typed, and the way back out of a search was gone from
 * the place it should have been listed.
 *
 * Empty spaces stay for the same reason — a floor nobody has pointed a camera at is exactly
 * what an installer opens this rail to find.
 */
export const selectCameraScopeTree = createSelector(
	[selectCameras, selectSites, selectFloors],
	(cameras, sites, floors) => buildTree(cameras, sites, floors, { withEmptySpaces: true })
);

/**
 * Applies one space's saved tile order.
 *
 * 🛑 Anything the order does not mention keeps its natural place BEHIND the cameras it
 * does. A saved order is a snapshot of the cameras that existed when the operator
 * dragged; filtering the list down to it would make the next sync's camera disappear
 * from the wall, and rebuilding the order on every list change would undo the drag.
 */
const applyTileOrder = (cameras, order) => {
	if (!order?.length) return cameras;
	const rank = new Map(order.map((deviceID, index) => [deviceID, index]));
	const moved = cameras
		.filter((camera) => rank.has(camera.deviceID))
		.sort((a, b) => rank.get(a.deviceID) - rank.get(b.deviceID));
	const untouched = cameras.filter((camera) => !rank.has(camera.deviceID));
	return [...moved, ...untouched];
};

/**
 * The wall: the same Site › Space tree the browser draws, in the order the operator
 * dragged the tiles into.
 *
 * 🛑 The order is the ONLY difference from selectCameraTree, and it is presentation and
 * nothing else — a drag never moves a camera between spaces, never touches a floor plan
 * and never writes to the server. It exists so three cameras worth watching together can
 * be put side by side for as long as the tab is open.
 */
export const selectCameraWall = createSelector([selectCameraTree, selectUi], (tree, ui) =>
	tree
		// 🛑 The rail's narrowing is applied HERE and nowhere upstream. The rail draws its own
		// rows FROM the tree, so a tree that already hid the other groups would leave the
		// operator holding a filter they cannot undo — one row, the one they picked, and no
		// way back to the rest.
		//
		// One key does both levels: a site key ("360") keeps that whole site, a space key
		// ("360:812") keeps one space of it. They cannot contradict each other because there
		// is only ever one.
		.filter((site) => !ui.groupKey || ui.groupKey === String(site.key) || ui.groupKey.startsWith(`${site.key}:`))
		.map((site) => ({
			...site,
			spaces: site.spaces
				.filter((space) => !ui.groupKey || ui.groupKey === String(site.key) || space.key === ui.groupKey)
				// 🛑 An empty space is drawn ONLY when it is the one being asked for. The tree
				// carries every space the site has so the RAIL can list them — that list is how
				// an installer sees a floor nobody has pointed a camera at — but drawing them
				// all on the wall put a row of "no cameras in this space" between the tiles
				// somebody came to look at. Picking one in the rail is the request; that is
				// when the wall answers it, with the empty space itself rather than a blank
				// page.
				.filter((space) => space.cameras.length > 0 || ui.groupKey === space.key)
				.map((space) => ({ ...space, cameras: applyTileOrder(space.cameras, ui.order[space.key]) }))
		}))
		// Recounted against what SURVIVED, so a site heading cannot say "2 spaces" over one
		// space of two. ⚠️ Sites are NOT dropped for holding no cameras: picking an empty
		// space in the rail is a legitimate thing to be looking at, and dropping its site
		// would answer that click with a blank page.
		.map((site) => ({ ...site, count: site.spaces.reduce((total, space) => total + space.cameras.length, 0) }))
);

/**
 * The first row of the browser as DRAWN. Taken from the tree, not the server's order —
 * otherwise the selection highlights a row somewhere down the list.
 */
export const selectFirstTreeCameraID = createSelector([selectCameraTree], (tree) => {
	// 🛑 The first CAMERA, not the first space's first camera. Spaces with no cameras are
	// drawn now, and one of them sorting first left this null — so the page opened with
	// nothing selected on an org whose empty floor happened to be alphabetically first.
	for (const site of tree) {
		for (const space of site.spaces) {
			if (space.cameras.length) return space.cameras[0].deviceID;
		}
	}
	return null;
});

// ── The window ──────────────────────────────────────────────────────────────────

/**
 * Whether a range crosses midnight — which is the only thing that decides whether a time
 * on this surface needs a date beside it. "18:00 → 07:00" is unreadable without one.
 */
const spansDays = (start, end) => !start.isSame(end, 'day');

/** HH:mm inside one day, "Tue 18:00" across days. One rule, so no two labels disagree. */
const stamp = (instant, withDate) => instant.format(withDate ? 'ddd HH:mm' : 'HH:mm');

/**
 * The one window every surface reads: the timeline, the picker, the player, the event
 * table and the .xlsx. One derivation, so they cannot describe different periods.
 *
 * 🛑 It is two INSTANTS. It used to be a day plus two offsets into a 96-bucket grid, and
 * that made the ordinary request — "yesterday evening until tonight" — unrepresentable:
 * every window had to start and end inside one calendar day. Nothing here knows about days
 * any more; `spansDays` only decides how a time is written down.
 */
export const selectWindow = createSelector([selectUi], (ui) => {
	const now = dayjs();
	// The default is resolved HERE, not stored: an instant frozen into initialState at
	// import time is stale by the time anyone opens the modal.
	//
	// 🛑 SNAPPED to the bucket grid, though. A bare `now` moves on every recomputation, so
	// the derived range — and the events cache key built from it — would be a different
	// value each time anything at all changed, re-requesting the same seconds under a key
	// that can never hit. Snapped, the fallback is stable for a quarter of an hour.
	const end = ui.to ? dayjs(ui.to) : now.subtract(now.minute() % BUCKET_MINUTES, 'minute').startOf('minute');
	const start = ui.from ? dayjs(ui.from) : end.subtract(HISTORY_CHUNK_MINUTES, 'minute');

	const durationMins = Math.max(0, end.diff(start, 'minute'));
	const dated = spansDays(start, end);

	return {
		// ISO 8601 — what every v5 date range takes. No epoch maths in the browser.
		startDate: start.toISOString(),
		endDate: end.toISOString(),
		startLabel: stamp(start, dated),
		endLabel: stamp(end, dated),
		spansDays: dated,
		// True when the end IS the live edge (within the grid step). The surface says "now"
		// rather than a time that is already stale by the time it is read.
		endsNow: now.diff(end, 'minute') < BUCKET_MINUTES,
		durationMins,
		overOneHour: durationMins > HISTORY_CHUNK_MINUTES,
		// How many requests the FOOTAGE costs. Not a thing anyone is asked to think about —
		// the vendor caps a playback request at an hour and nothing stitches the results, so
		// the player walks the list while the range stays one range.
		parts: Math.max(1, Math.ceil(durationMins / HISTORY_CHUNK_MINUTES))
	};
});

/**
 * IS THE PICTURE THE CURRENT ONE? The one place this is decided, for every surface.
 *
 * 🛑 DERIVED, never stored. There used to be a `ui.tab` of 'live' | 'history' set by two
 * buttons, and it was a third opinion about a thing the window already said: a window whose
 * end is the live edge IS live, and one dragged into the past IS playback. Two facts where
 * there is one fact drift — the tab said `live` while the window sat on Tuesday morning,
 * and whichever surface read the wrong one was simply wrong about what was on screen.
 *
 * 🛑 `seekTo` is the second half and it is not a special case — it is the whole reason the
 * rule is not just `endsNow`. Clicking an event row in the last hour asks to REPLAY a
 * moment inside a window that does end now, so "the window reaches the live edge" alone
 * would answer "live" to a request for playback and hand back a live stream instead. The
 * two inputs are exactly the two ways to leave live: move the window off the edge, or ask
 * for a moment inside it.
 *
 * Coming back is the mirror image, and `goLive` does both halves at once — see useCameraMode,
 * which is what the state chip on the picture presses.
 */
export const selectIsLive = createSelector(
	[selectWindow, selectUi],
	/*
	 * 🛑 THREE inputs, and `ui.to` is the third. A window whose end is the live edge is only
	 * live when nobody ASKED for that end: dragging a period that runs up to now — the most
	 * ordinary request there is, "the last ten minutes, something just happened" — used to
	 * be read as live, and the player answered with the live stream instead of the period
	 * the operator had just drawn. An explicit end IS the answer to "did they choose this".
	 */
	(window, ui) => window.endsNow && ui.seekTo == null && ui.to == null
);

/**
 * WHAT IS FETCHED: the whole days the range touches, and nothing else about them.
 *
 * 🛑 IT MUST NOT DEPEND ON WHERE INSIDE THE DAY THE WINDOW SITS, and that is load-bearing
 * rather than a nicety. The events request is keyed on THIS, not on the window (see
 * selectEventsKey), so a context that shifted with the window would mint a new key on every
 * pixel of a gesture and drop the previous result mid-drag. Whole calendar days are the one
 * anchor a hand cannot move.
 *
 * 🛑 IT USED TO CARRY A BAR WIDTH AND A BAR COUNT — `step`, `count`, `multiDay`, off a ladder
 * of rungs snapped to a 120-bar budget. Those were about DRAWING: they belonged to a recharts
 * chart of fifteen-minute bars and to the two selectors that fed it. The scrub bar that
 * replaced it draws motion as the runs actually happened (see selectMotionRuns) and rules its
 * own ticks off the clock (see scrubberTime), so nothing reads a bar width any more. What is
 * left is the only question this ever answered for the wire: which days to ask for.
 *
 * ⚠️ This is why the server's window cap is 48 h while the operator may only pick 24: a window
 * straddling midnight rounds out to two whole days. A 24 h box centred on the window would be
 * a cheaper read and was tried; it moved with the window, which is exactly the failure above.
 */
export const selectTimelineContext = createSelector([selectWindow], (window) => ({
	startDate: dayjs(window.startDate).startOf('day').toISOString(),
	// Exclusive end — midnight after the last day the range touches.
	endDate: dayjs(window.endDate).startOf('day').add(1, 'day').toISOString()
}));

/** How much breathing room a magnified span gets, at least, on each side. */
export const VIEW_PAD_MINUTES = 10;

/**
 * THE VIEW TO MAGNIFY A SPAN IN — used by the two gestures that point at something far
 * smaller than the chart is drawing.
 *
 * 🛑 WITHOUT THE ZOOM BOTH ARE UNUSABLE, and for one reason: the chart draws whatever is
 * loaded, which is a whole day, while the things picked out of it are minutes long.
 *   - The BRACKET caps at twenty minutes, so unzoomed its two edges land within a bar or
 *     two of each other — two handles nobody can tell apart, let alone take hold of one of.
 *   - An EVENT is often seconds. Clicking its row painted a band a fraction of a bar wide
 *     somewhere in a day: the answer to "where is what I just pressed" was invisible at the
 *     scale the answer was given at.
 *
 * Padding is HALF THE SPAN either side, floored at ten minutes, so the span stays about half
 * the width of the chart whatever its length — big enough to read, with room either way to
 * see what is just outside it.
 *
 * ⚠️ Deliberately NOT clamped, and nothing downstream clamps it into the loaded days either —
 * the scrub bar only takes a SPAN from this (see SeekTimeCameraScrubber) and travel reaches as
 * far back as retention, not as far back as what has been fetched.
 */
export const viewAround = (startMs, endMs) => {
	const pad = Math.max((endMs - startMs) / 2, VIEW_PAD_MINUTES * 60_000);
	return { startMs: startMs - pad, endMs: endMs + pad };
};

/**
 * WHERE THE WINDOW HAS TO BE FOR A MOMENT TO BE PLAYABLE.
 *
 * 🛑 THE CHART IS WIDER THAN THE WINDOW, ALWAYS, and that gap is what made clicking it look
 * broken. The bars cover whole days — the selection is only meaningful against the quiet
 * hours either side of it — while the window is an hour somewhere inside them, and the player
 * holds clips for the WINDOW alone (selectWindowParts). So a click on this morning, with the
 * window on this afternoon, asked to replay a second no loaded clip contained: the page dropped
 * out of live, because a seek had been set, and then went on playing the same afternoon.
 * Nothing on screen changed except the mode.
 *
 * Inside the window there is nothing to do — the footage is already loaded, and re-anchoring
 * would throw away a period the operator drew to answer a question they are still asking.
 * Outside it, the window MOVES to start on the instant pressed and keeps its length: "same
 * question, earlier", the same reading a click already had in clip mode (see `commit`).
 *
 * ⚠️ THE BRACKET IS NOT ITS BUSINESS ANY MORE. This answers for the WINDOW — what has to be
 * fetched for a moment to be playable — and the amber band is a separate period now (see
 * `ui.clipRange`), which is exactly why moving the band no longer re-filters anything.
 *
 * ⚠️ Floored at `minMs` so a window narrowed to a couple of minutes still lands on something
 * playable. ⚠️ NOT clamped here: `clampRange` in the slice is the one place a range is made
 * askable, and clamping twice means two rules that can disagree.
 */
export const rangeForMoment = (atMs, startMs, endMs, minMs) => {
	if (atMs >= startMs && atMs < endMs) return { kind: 'inside' };
	return { kind: 'window', startMs: atMs, endMs: atMs + Math.max(endMs - startMs, minMs) };
};

/*
 * 🛑 GONE: `selectTimelineView` and `selectTimelineScale`.
 *
 * They were the two halves of drawing a CHART. The view narrowed the loaded days by the
 * operator's zoom so the bars could be re-bucketed in the browser; the scale turned that view
 * into BAR INDICES, because a chart is addressed in bars — a click landed on bar 37, not on
 * 09:15 — and two sliders under it moved those indices.
 *
 * The scrub bar that replaced the chart has neither vocabulary. It has no view of its own: the
 * centre of the bar IS the frame on screen, and the only thing left of a "zoom" is how much time
 * the bar shows — a SPAN, read straight off `ui.timelineZoom` and clamped in the component (see
 * SeekTimeCameraScrubber). And it addresses instants, not indices, which is the whole reason it
 * can seek to a second instead of to a quarter of an hour.
 *
 * ⚠️ `ui.timelineZoom` SURVIVES them and is still written by two controls — an event row and
 * arming the bracket (see CreateClipCameraButton) — which says "magnify to
 * about this much". Their width is honoured; their centre cannot be, because the centre is
 * playback.
 */

/**
 * The window split into playable chunks, each within the provider's cap.
 *
 * The player walks this list; asking for the whole window in one call earns a 500
 * with a sentence about splitting it.
 */
export const selectWindowParts = createSelector([selectWindow], (window) => {
	const parts = [];
	for (let offset = 0; offset < window.durationMins; offset += HISTORY_CHUNK_MINUTES) {
		const start = dayjs(window.startDate).add(offset, 'minute');
		const minutes = Math.min(HISTORY_CHUNK_MINUTES, window.durationMins - offset);
		const end = start.add(minutes, 'minute');
		parts.push({
			startDate: start.toISOString(),
			endDate: end.toISOString(),
			// Across days a bare "14:00 – 15:00" appears twice in the same list and names two
			// different hours; the day is what tells them apart.
			label: `${stamp(start, window.spansDays)} – ${end.format('HH:mm')}`
		});
	}
	return parts;
});

// ── Events ──────────────────────────────────────────────────────────────────────

/**
 * The request behind everything in the modal: the selected camera over the days the range
 * touches — the CONTEXT, not the window.
 *
 * The chart needs the quiet hours either side of the selection to be worth drawing, and
 * keying the fetch to whole days is also what makes the handles free to move: dragging
 * inside a day changes the window, not the request.
 */
export const selectEventsKey = createSelector([selectUi, selectTimelineContext], (ui, context) => {
	if (ui.selectedDeviceID == null) return null;

	// 🛑 Never past NOW, and SNAPPED to the step. The context runs to midnight, which for
	// today is a request for hours that have not happened; and an unsnapped `now` would mint
	// a new key on every store tick, re-requesting the same seconds under a key that can
	// never hit — which is what locked the page up.
	const now = dayjs();
	const snapped = now.subtract(now.minute() % BUCKET_MINUTES, 'minute').startOf('minute');
	const end = dayjs(context.endDate).isAfter(snapped) ? snapped : dayjs(context.endDate);

	return {
		deviceID: ui.selectedDeviceID,
		startDate: context.startDate,
		endDate: end.toISOString()
	};
});

const selectRangeEventsResult = createSelector([selectCamerasState, selectEventsKey], (vision, key) =>
	key ? vision.events[eventsKey(key.deviceID, key.startDate, key.endDate)] || null : null
);

export const selectEventsLoading = createSelector([selectCamerasState, selectEventsKey], (vision, key) =>
	key ? vision.loading.events[eventsKey(key.deviceID, key.startDate, key.endDate)] === true : false
);

/*
 * 🛑 GONE with the events list: `EVENT_KINDS`, `toEventRow` and `selectEventRows`.
 * The overlap rule they carried lives on in `selectMotionRuns`, which shades the scrub bar.
 */

/*
 * 🛑 GONE: `selectTimelineBuckets`.
 *
 * It counted every detection run into each fifteen-minute cell it touched, so a chart could give
 * the cell a height — and it carried `inWindow` and `inHighlight` so the bars, the band and its
 * two edges could not disagree about what was selected.
 *
 * A grid is what a bar chart needs and it is exactly what a scrub bar cannot use: at the zooms
 * this bar reaches — down to half a minute — one cell is the whole picture, and what is selected
 * is said by the band and the head rather than by a colour. `selectMotionRuns` below is the
 * replacement, and it is the simpler of the two: presences as the spans they actually were.
 */

/**
 * EVERY PRESENCE THE CAMERA REPORTED, as spans of wall-clock time.
 *
 * 🛑 RAW RUNS, NOT BUCKETS, and that is the difference between a strip and a chart. The bars
 * needed a grid — every event counted into whichever fifteen-minute cell it touched — because
 * a bar has to have a width and a height. A strip under a scrub bar has neither: a presence
 * from 09:03:12 to 09:04:40 is a mark eighty-eight seconds long, drawn where those eighty-eight
 * seconds are. At the zooms this bar reaches, a fifteen-minute cell is the whole picture.
 *
 * 🛑 MILLISECONDS, so the caller never converts. The server speaks epoch SECONDS and the two
 * units in one component is how a mark ends up in 1970.
 *
 * ⚠️ The runs cover the LOADED DAYS (see selectEventsKey), not the window and not what the bar
 * happens to be showing — so panning into a day nobody has fetched draws no marks rather than
 * wrong ones. It fills in when the window lands there and the read returns.
 *
 * ⚠️ `until` is optional: a single sample has no duration. Those are kept as zero-length runs
 * and the strip gives them a minimum width, because "something was seen here" is the whole
 * point of the mark.
 */
export const selectMotionRuns = createSelector([selectRangeEventsResult], (result) =>
	!result
		? []
		: result.events.map((event) => ({
				fromMs: event.at * 1000,
				toMs: (event.until ?? event.at) * 1000
			}))
);

/*
 * 🛑 GONE with the events list: `selectEventsNotice`, which captioned its empty state.
 * Nothing replaces it — an empty hour shows as an unshaded stretch of the scrub bar.
 */

// ── Streams ─────────────────────────────────────────────────────────────────────

/**
 * How old a still may be before the wall asks for a fresh one.
 *
 * 🛑 The whole point of a picture on a tile is that it is roughly what the camera can see
 * NOW. A tab left open all afternoon under a frame from lunchtime is worse than no frame
 * at all — it is the one failure the operator cannot detect by looking. Two minutes is
 * short enough that the wall stays honest and long enough that scrolling up and down a
 * long site does not re-ask for every tile.
 */
export const THUMBNAIL_MAX_AGE_SECONDS = 120;

export const selectThumbnails = (state) => state.Cameras.thumbnails;

export const selectThumbnailByDeviceID = (state, deviceID) => state.Cameras.thumbnails[deviceID] || null;

/**
 * Whether this camera's still is worth asking for, at the instant `now`.
 *
 * ⚠️ `now` is an ARGUMENT rather than a `Date.now()` inside. A selector that reads the
 * clock returns a different answer on every recomputation, so anything memoised on it
 * re-runs forever — and this one decides whether to fire a request.
 *
 * @param {Object} thumbnail - the stored entry, or null
 * @param {boolean} loading - whether a request for it is already in flight
 * @param {number} now - epoch SECONDS
 */
export const thumbnailNeedsFetch = (thumbnail, loading, now) => {
	if (loading) return false;
	if (!thumbnail) return true;
	// A camera that answered with an error is not asked again. The endpoint is either
	// there for this vendor or it is not, and retrying on every scroll turns one 404 into
	// a request per tile per pass.
	if (thumbnail.error) return false;
	if (!thumbnail.capturedAt) return false;
	return now - Math.floor(new Date(thumbnail.capturedAt).getTime() / 1000) > THUMBNAIL_MAX_AGE_SECONDS;
};

export const selectStreamByDeviceID = (state, deviceID) => state.Cameras.streams[deviceID] || null;

export const selectStreamLoading = (state, deviceID) => state.Cameras.loading.stream[deviceID] === true;

/**
 * Is the descriptor about to die?
 *
 * `expiresAt` on the HLS path is a hard 30-minute wall with no refresh endpoint, so
 * a stream that is not re-requested stops mid-view. A minute of headroom means the
 * new one is playing before the old one lapses.
 */
export const selectStreamIsStale = (state, deviceID) => {
	const stream = state.Cameras.streams[deviceID];
	if (!stream?.expiresAt) return false;
	return Math.floor(Date.now() / 1000) > stream.expiresAt - 60;
};

/**
 * Does this camera need a descriptor minted, or will the one we hold do?
 *
 * 🛑 The question is "needs minting", NOT "is reusable", and they are not opposites. A
 * FAILED attempt is not reusable and must not be re-minted either: an error entry that
 * reads as "needs minting" makes the player ask, fail and ask again on every render.
 *
 * ⚠️ It takes no window any more. One live descriptor answers every window, because
 * resolvePlayback rewrites the times on the url rather than asking for another.
 */
export const selectStreamNeedsMint = (state, deviceID) => {
	const stream = state.Cameras.streams[deviceID];
	if (!stream) return true;
	if (stream.error) return false;
	return selectStreamIsStale(state, deviceID);
};

/**
 * A url for live, for a past window, or the sentence saying why there is neither.
 *
 * 🛑 The two refusals below are the last guard before Verkada: a camera without `history`
 * would answer last Tuesday with a silent live stream, and a window over the vendor's cap
 * gets a 500 that reads as broken hardware. The cap is read off the camera, never hardcoded.
 *
 * ⚠️ A plain function, not a selector: it returns a fresh object, which through
 * useSelector re-renders on every store tick. Callers wrap it in useMemo.
 *
 * @param {Object|null} stream - the descriptor from selectStreamByDeviceID
 * @param {Object|null} camera - the camera row (capabilities + the provider's cap)
 * @param {Object|null} [playWindow] - { startDate, endDate } ISO, or null for live
 * @returns {{url: string|null, error: string|null, streamType: string|null, descriptor: Object|null}}
 */
export const resolvePlayback = (stream, camera, playWindow = null) => {
	const none = { url: null, error: null, streamType: null, descriptor: null };
	if (!camera || !stream) return none;
	if (stream.error) return { ...none, error: stream.error };

	// SDK providers have no url to build — the descriptor IS the input set.
	if (stream.streamType !== 'hls' || !stream.jwt) {
		return { ...none, streamType: stream.streamType ?? null, descriptor: stream };
	}

	const shape = { ...none, streamType: 'hls', descriptor: stream };

	// The server hands back the credential and the ids it was minted against; the URL is
	// assembled here. One builder, on the side that decides what to ask for.
	const parts = {
		baseUrl: stream.baseUrl,
		verkadaOrgID: stream.verkadaOrgID,
		cameraID: stream.cameraID,
		jwt: stream.jwt
	};

	// 🛑 The stream parameters are decided HERE, not by whoever minted the token. The
	// server hands back a url because that is the shape of its endpoint, but the only
	// part of it we keep is the credential — see buildVerkadaStreamUrl. Codec and resolution are
	// the two levers on live latency and picture size, and neither should need a server
	// deploy to move.
	if (!playWindow?.startDate || !playWindow?.endDate) {
		return { ...shape, url: buildVerkadaStreamUrl({ ...parts, codec: LIVE_CODEC, resolution: LIVE_RESOLUTION }) };
	}

	if (!camera.capabilities?.history) {
		return { ...shape, error: 'This camera plays live only — it cannot replay a past window.' };
	}

	const from = Math.floor(new Date(playWindow.startDate).getTime() / 1000);
	const to = Math.floor(new Date(playWindow.endDate).getTime() / 1000);
	const cap = camera.maxHistoryWindowSeconds;
	if (cap && to - from > cap) {
		const maxMinutes = Math.floor(cap / 60);
		return {
			...shape,
			error: `This camera plays at most ${maxMinutes} minutes per request — ask for a shorter window, or split it.`
		};
	}

	// History keeps high_res: there the resolution is a PRIORITISATION hint, not a direct
	// quality choice — low_res means "only low resolution footage", which is the wrong
	// answer when someone is looking for what happened.
	return { ...shape, url: buildVerkadaStreamUrl({ ...parts, from, to, codec: LIVE_CODEC, resolution: 'high_res' }) };
};

/** Is the scope ONE site, rather than the whole portfolio? Same key the buckets use. */
export const selectIsSiteScoped = createSelector([selectScopeKey], (siteID) => Boolean(siteID) && siteID !== 'all');

/** The header line: "2 sites · 2 spaces · 8 cameras · 2 need placing". */
export const selectPageSubtitle = createSelector(
	[selectMetrics, selectConnectionState, selectIsSiteScoped],
	(metrics, connection, siteScoped) => {
		if (connection === 'none') return 'No camera source connected';
		// Deliberately NOT "connected — nothing synced yet". At this stage the page does
		// not surface that distinction anywhere else (the setup card is parked), so a
		// header line that alone reports it points at a Sync button nobody can see.
		if (!metrics || connection === 'synced-never') return 'No cameras yet';

		const parts = [];
		// The site count is a portfolio fact. Inside one site it is always "1 site", which
		// tells the operator where they already know they are.
		if (!siteScoped) parts.push(`${metrics.siteCount} site${metrics.siteCount === 1 ? '' : 's'}`);
		parts.push(`${metrics.spaceCount} space${metrics.spaceCount === 1 ? '' : 's'}`);
		parts.push(`${metrics.total} camera${metrics.total === 1 ? '' : 's'}`);
		if (metrics.offPlan > 0) parts.push(`${metrics.offPlan} need placing`);
		return parts.join(' · ');
	}
);

/* ── Clips ──────────────────────────────────────────────────────────────────────────────── */

const selectClips = (state) => state.Cameras.clips;

/** One camera's clips, newest first, hydrated from `byID`. */
export const selectClipRows = createSelector([selectClips, (_state, deviceID) => deviceID], (clips, deviceID) =>
	(clips.forDevice[deviceID]?.ids || []).map((id) => clips.byID[id]).filter(Boolean)
);

export const selectClipsLoading = (state, deviceID) => selectClips(state).forDevice[deviceID]?.loading === true;

export const selectClipRequesting = (state, deviceID) => selectClips(state).requesting[deviceID] === true;

/**
 * The clips this session asked for and is still following — the ones the poll saga owns (see
 * `watchClip`). Returns the array in state, so the reference is stable between changes.
 */
export const selectWatchedClipIDs = (state) => selectClips(state).watching;

/**
 * Is the selected window askable as a clip?
 *
 * 🛑 Three conditions, and the LENGTH is the one that bites: MAX_RANGE_DAYS is a day and the
 * cap is 20 minutes, so the ordinary window is far too long. Gating here is what stops the
 * control handing the server a request it will refuse.
 */
/**
 * THE PERIOD THE CLIP WILL COVER, resolved — or null while nothing is bracketed.
 *
 * 🛑 A SEPARATE PERIOD FROM THE WINDOW, and the separation is the point. The band used to be
 * `selectWindow` itself, so nudging it re-filtered the page: a new period, a new mint, a new
 * manifest, and playback thrown back to the top of it. Trimming a clip is a dozen small
 * adjustments and every one of them cost a reload. See `ui.clipRange`.
 */
export const selectClipRange = createSelector([selectCamerasUi], (ui) => {
	if (!ui.clipRange?.from || !ui.clipRange?.to) return null;
	const start = dayjs(ui.clipRange.from);
	const end = dayjs(ui.clipRange.to);
	return {
		startDate: ui.clipRange.from,
		endDate: ui.clipRange.to,
		startMs: start.valueOf(),
		endMs: end.valueOf(),
		durationMins: end.diff(start, 'minute', true)
	};
});

export const selectClipWindowState = createSelector(
	[selectClipRange, selectSelectedCamera, (state) => state],
	(clip, camera, state) => {
		const capable = camera ? selectCanExport(state, camera.deviceID) : false;
		const mins = clip?.durationMins ?? 0;
		const maxSeconds = camera ? selectMaxClipSeconds(state, camera.deviceID) : CLIP_MAX_MINUTES * 60;
		// ⚠️ Belt and braces: `clipRangeFrom` caps every bracket on the way into the store, so
		// this can only fire on state written before that rule existed. It stays because the
		// button's whole disabled state is this object, and a silent `true` there would offer a
		// cut the server refuses.
		const tooLong = mins > maxSeconds / 60;
		const empty = !(mins > 0);
		/*
		 * 🛑 BEING OFFLINE IS NOT A REFUSAL ANY MORE, and it used to be both a `false` here and a
		 * sentence saying "there is no footage to cut". That sentence was untrue of a RECORDING:
		 * a camera that went down an hour ago recorded everything up to the moment it did, and
		 * cutting a piece out of that is the most likely reason anyone opens a dead camera at all
		 * — the incident and the outage are usually the same event.
		 *
		 * ⚠️ What is gone is the browser pre-judging it. If the vendor cannot serve the footage
		 * after all, the refusal comes back from the server against a real request, on the clip
		 * row, instead of a disabled button asserting it in advance.
		 */
		return {
			capable,
			tooLong,
			empty,
			canExport: capable && !empty && !tooLong,
			reason: !capable
				? 'This camera keeps no recorded history to export.'
				: tooLong
					? maxSeconds < 60
						? `Maximum ${maxSeconds} seconds per clip on this camera.`
						: `Maximum ${maxSeconds / 60} minutes per clip.`
					: empty
						? 'Drag across the timeline to mark the clip.'
						: null
		};
	}
);
